import { useState } from "react";
import { MaskedLines, Reveal } from "../components/Reveal";
import { toast, Toaster } from "sonner";
import { Send, MapPin, Mail, Phone, ArrowUpRight } from "lucide-react";
import SocialWall from "../components/SocialWall";
import { motion, AnimatePresence } from "framer-motion";
import { JsonLd } from "../components/SEO";


const MAPS_LINK = "https://maps.app.goo.gl/jeUUwoCR5ByfWEnu8";

const MAP_EMBED =
  "https://maps.google.com/maps?q=31.6692891,74.8465331&z=15&output=embed";

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact Dorado9",
  url: "https://dorado9.com/contact",
  description:
    "Get in touch with Dorado9 for digital marketing, SEO, Google Ads, web development and creative services.",
  mainEntity: {
    "@type": "LocalBusiness",
    "@id": "https://dorado9.com/#organization",
    name: "Dorado9",
    url: "https://dorado9.com",
    email: "hello@dorado9.com",
    telephone: "+91 9855 844 729",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Amritsar",
      addressRegion: "Punjab",
      addressCountry: "IN",
    },
    areaServed: "India",
  },
};

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
  phone: "",
    message: "",
  });

  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill all fields.");
      return;
    }

    setSending(true);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: "e8c07b78-90c7-4c51-9134-a9a61bbe3994",

           name: form.name,
  email: form.email,
  phone: form.phone,
  message: form.message,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success("Message sent successfully!");

        setForm({
          name: "",
          email: "",
          phone: "",
          message: "",
        });
      } else {
        toast.error(result.message || "Something went wrong.");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <JsonLd id="contact-page" data={contactSchema} />
      <Toaster
        theme="dark"
        position="top-center"
        richColors
      />

      <section className="relative min-h-[100vh] pt-40 lg:pt-56 pb-24 px-6 lg:px-12 bg-brand-bg overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=60&auto=format&fit=crop"
            alt=""
            aria-hidden="true"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover opacity-[0.20] mix-blend-luminosity"
          />

          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(20,18,22,0.72) 0%, rgba(20,18,22,0.55) 40%, var(--bg) 100%)",
            }}
          />
        </div>

        <div
          className="absolute -top-40 right-[-10%] w-[60vw] h-[50vw] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(102,76,221,0.32), transparent 60%)",
          }}
        />

        <div className="absolute inset-0 grain opacity-25" />

        <div className="max-w-[1600px] mx-auto relative">
          <p className="eyebrow mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-brand-purple" />
            Contact
          </p>

          <h1 className="font-display text-[12vw] lg:text-[9vw] leading-[1.2] tracking-tighter uppercase">
            <MaskedLines
              lines={[
                "Your marketing",
                <>
                  guru{" "}
                  <span className="font-serif-accent text-brand-purple lowercase">
                    awaits.
                  </span>
                </>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-10 max-w-4xl text-zinc-400 text-lg leading-relaxed"
          >
            The best partnerships don't begin with contracts. They begin with
            curiosity, honest conversations, and a shared ambition to create
            something meaningful. If that sounds like your kind of
            collaboration, we'd love to hear from you.
          </motion.p>
        </div>
      </section>

            <section className="bg-brand-bg py-16 lg:py-24 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-16">

          <Reveal className="lg:col-span-5 space-y-10">

            <div>
              <p className="eyebrow mb-2">Studio</p>

              <p className="font-display text-2xl leading-tight">
                Dorado9
                <br />
                Amritsar, Punjab · India
              </p>
            </div>

            <div className="space-y-4">

              <a
                href="mailto:hello@dorado9.com"
                className="flex items-center gap-3 text-zinc-300 hover:text-brand-mint transition-colors"
              >
                <Mail size={16} />
                hello@dorado9.com
              </a>

              <a
                href="tel:+919356020762"
                className="flex items-center gap-3 text-zinc-300 hover:text-brand-mint transition-colors"
              >
                <Phone size={16} />
                +91 93560 20762
              </a>

              <p className="flex items-center gap-3 text-zinc-300">
                <MapPin size={16} />
                Amritsar, Punjab
              </p>

            </div>

            <div className="border-t border-zinc-900 pt-5">

              <p className="eyebrow mb-4">
                Hours
              </p>

              <p className="text-zinc-400">
                Mon – Fri · 10:00 – 19:00 IST
              </p>

              <p className="text-zinc-400">
                Sat · By appointment
              </p>

            </div>

            <p className="font-serif-accent text-brand-mint text-4xl leading-none">
              Every great brand starts with a conversation.
            </p>

          </Reveal>

          <Reveal
            delay={0.15}
            className="lg:col-span-7"
          >

            <form
              onSubmit={submit}
              className="space-y-6"
            >

              <div className="grid md:grid-cols-2 gap-6">

                <Field
                  label="Your Name"
                  value={form.name}
                  onChange={(v) =>
                    setForm({ ...form, name: v })
                  }
                />

                <Field
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(v) =>
                    setForm({ ...form, email: v })
                  }
                />

              </div>

              <Field
                label="phone"
                value={form.phone}
                onChange={(v) =>
                  setForm({ ...form, phone: v })
                }
              />

              <div>

                <label className="eyebrow block mb-3">
                  Message *
                </label>

                <textarea
                  rows={6}
                  value={form.message}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      message: e.target.value,
                    })
                  }
                  className="w-full bg-transparent border-b border-zinc-800 focus:border-brand-mint outline-none py-3 text-zinc-100 resize-none transition-colors"
                  placeholder="Tell us about your goals..."
                />

              </div>

              <button
                type="submit"
                disabled={sending}
                className="btn-primary"
              >
                {sending ? "Sending..." : "Send message"}

                <Send size={16} />
              </button>

            </form>

          </Reveal>

        </div>
      </section>
      {/* SOCIAL WALL */}
      <SocialWall />
            {/* INTERACTIVE MAP */}
      <section className="relative border-y border-[color:var(--border)] overflow-hidden" data-testid="contact-map-section">
        <div className="relative h-[600px] lg:h-[680px]">
          {/* Interactive Google Map, recoloured to match the dark brand */}
          <iframe
            title="Dorado9 — Gumtala, Amritsar"
            src={MAP_EMBED}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="dorado-map__frame absolute inset-0 w-full h-full"
            data-testid="contact-map-iframe"
            
          />

          {/* Left Gradient */}
          <div
            className="hidden lg:block absolute left-0 top-0 h-full w-[44%] pointer-events-none z-10"
            style={{
              background:
                "linear-gradient(90deg, rgba(20,18,22,0.95) 0%, rgba(20,18,22,0.78) 45%, rgba(20,18,22,0.30) 78%, transparent 100%)",
            }}
          />

          {/* Mobile Overlay */}
          <div
            className="lg:hidden absolute inset-0 pointer-events-none z-10"
            style={{
              background: "rgba(20,18,22,0.74)",
            }}
          />

          {/* Purple Glow */}
          <div
            className="absolute inset-0 pointer-events-none z-10"
            style={{
              background:
                "radial-gradient(circle at 20% 30%, rgba(102,76,221,0.18), transparent 55%)",
            }}
          />

          {/* Grain */}
          <div className="absolute inset-0 grain opacity-[0.12] pointer-events-none z-10" />

          {/* Content */}
          <div className="absolute left-0 top-0 z-20 h-full flex items-center px-6 lg:px-16">

            <Reveal className="max-w-md">

              <p className="eyebrow mb-6 flex items-center gap-3">
                <span className="w-8 h-px bg-brand-mint" />
                Visit our studio
              </p>

              <h2 className="font-display text-5xl lg:text-7xl uppercase leading-[0.9] mb-10">
                Let's{" "}
                <span className="font-serif-accent text-brand-mint lowercase">
                  meet.
                </span>
              </h2>

              <div className="space-y-7">

                <div className="flex gap-4">
                  <MapPin
                    size={18}
                    className="text-brand-mint mt-1 shrink-0"
                  />

                  <div>
                    <p className="eyebrow mb-1">
                      Office
                    </p>

                    <p className="text-zinc-300 leading-relaxed">
                     Hno. 94, 95, Lane Number 6, Gumtala Sub Urban 
                      <br />
                      Sahibzada Jujhar Singh Avenue
                      <br />
                       Gumtala, Amritsar 
                      <br />
                      Punjab 143001
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Mail
                    size={18}
                    className="text-brand-mint mt-1 shrink-0"
                  />

                  <div>
                    <p className="eyebrow mb-1">
                      Email
                    </p>

                    <a
                      href="mailto:hello@dorado9.com"
                      className="text-zinc-300 hover:text-brand-mint transition-colors"
                    >
                      hello@dorado9.com
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Phone
                    size={18}
                    className="text-brand-mint mt-1 shrink-0"
                  />

                  <div>
                    <p className="eyebrow mb-1">
                      Phone
                    </p>

                    <a
                      href="tel:+919356020762"
                      className="text-zinc-300 hover:text-brand-mint transition-colors"
                    >
                      +91 93560 20762
                    </a>
                  </div>
                </div>

              </div>

              <a
                href={MAPS_LINK}
                target="_blank"
                rel="noreferrer"
                className="btn-primary mt-10 inline-flex"
              >
                Get directions
                <ArrowUpRight size={16} />
              </a>

            </Reveal>

          </div>

        </div>
      </section>
    </>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
}) {
  return (
    <div>
      <label className="eyebrow block mb-3">
        {label} *
      </label>

      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-zinc-800 focus:border-brand-mint outline-none py-3 text-zinc-100 transition-colors"
      />
    </div>
  );
}